import {
  Directive,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
} from '@angular/core';

@Directive({
  selector: '[uiSwipeSheetClose],[uiSwipeSheetOpen]',
  standalone: true,
})
export class UiSwipeDirective implements OnInit, OnDestroy {
  public uiSwipeTrigger = output<void>();
  public settleMs = input<number>(180);
  public sheetThresholdRatio = input<number>(0.25);
  public sheetThresholdPx = input<number>(120);
  public swipeTransformSelector = input<string | null>(null);
  public swipeZones = input<string>('.header, .handle');

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);

  private active = false;
  private dy = 0;
  private startY = 0;
  private el: HTMLElement | null = null;
  private rafId: number | null = null;
  private pendingY: number | null = null;
  private settleAnim: Animation | null = null;

  public ngOnInit(): void {
    const host = this.host.nativeElement as HTMLElement;
    if (!host) return;

    this.zone.runOutsideAngular(() => {
      host.addEventListener('touchstart', this.onStart, {
        passive: true,
      });
      host.addEventListener('touchmove', this.onMove, {
        passive: false,
      });
      host.addEventListener('touchend', this.onEnd, { passive: true });
      host.addEventListener('touchcancel', this.onEnd, {
        passive: true,
      });
    });
  }

  public ngOnDestroy(): void {
    this.cancelRaf();
    this.settleAnim?.cancel();
    const host = this.host.nativeElement as HTMLElement;
    if (!host) return;
    host.removeEventListener('touchstart', this.onStart);
    host.removeEventListener('touchmove', this.onMove);
    host.removeEventListener('touchend', this.onEnd);
    host.removeEventListener('touchcancel', this.onEnd);
  }

  private get closeMode(): boolean {
    return !(this.host.nativeElement as HTMLElement).hasAttribute(
      'uiSwipeSheetOpen',
    );
  }

  private resolveEl(): HTMLElement {
    if (this.el) return this.el;
    const selector = this.swipeTransformSelector();
    if (selector) {
      const found = (
        this.host.nativeElement as HTMLElement
      ).querySelector(selector) as HTMLElement | null;
      if (found) {
        this.el = found;
        return found;
      }
    }
    this.el = this.host.nativeElement as HTMLElement;
    return this.el;
  }

  private onStart = (e: TouchEvent): void => {
    if (e.touches.length !== 1) return;

    const target = e.target as HTMLElement;
    const host = this.host.nativeElement as HTMLElement;
    const zones = host.querySelectorAll(this.swipeZones());
    let hit = false;
    zones.forEach((z) => {
      if (z.contains(target)) hit = true;
    });
    if (!hit) return;

    this.settleAnim?.cancel();
    this.settleAnim = null;
    this.cancelRaf();

    this.startY = e.touches[0].clientY;
    this.active = true;
    this.dy = 0;

    const el = this.resolveEl();
    el.style.animation = 'none';
    el.style.transition = 'none';
    el.style.transform = 'translate3d(0,0,0)';
  };

  private onMove = (e: TouchEvent): void => {
    if (!this.active || e.touches.length !== 1) return;

    const raw = e.touches[0].clientY - this.startY;
    const el = this.resolveEl();
    const limit = el.clientHeight || window.innerHeight;
    const close = this.closeMode;

    this.dy = close
      ? Math.max(0, Math.min(raw, limit))
      : Math.min(0, Math.max(raw, -limit));

    this.pendingY = this.dy;
    if (this.rafId === null) {
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        if (this.pendingY !== null) {
          el.style.transform = `translate3d(0,${Math.round(this.pendingY)}px,0)`;
          this.pendingY = null;
        }
      });
    }

    if ((close && this.dy > 0) || (!close && this.dy < 0)) {
      e.preventDefault();
    }
  };

  private onEnd = (): void => {
    if (!this.active) return;
    this.cancelRaf();
    this.active = false;

    const el = this.resolveEl();
    const height = el.clientHeight || window.innerHeight;
    const abs = Math.abs(this.dy);
    const close = this.closeMode;
    const closing = close ? this.dy > 0 : this.dy < 0;
    const trigger =
      closing &&
      (abs > height * this.sheetThresholdRatio() ||
        abs > this.sheetThresholdPx());

    if (trigger) {
      const target = close ? height : -height;
      this.animateSettle(el, this.dy, target, () => {
        this.zone.run(() => this.uiSwipeTrigger.emit());
        el.style.transform = 'translate3d(0,0,0)';
        this.dy = 0;
      });
    } else {
      this.animateSettle(el, this.dy, 0, () => {
        this.dy = 0;
      });
    }
  };

  /**
   * Web Animations API settle — explicit from/to keyframes
   * avoid the offsetHeight reflow hack and setTimeout drift.
   */
  private animateSettle(
    el: HTMLElement,
    from: number,
    to: number,
    done: () => void,
  ): void {
    // Set inline style to the target value. When animation ends
    // (no fill-mode), the element falls back to this seamlessly.
    el.style.transition = 'none';
    el.style.transform = `translate3d(0,${to}px,0)`;

    this.settleAnim = el.animate(
      [
        { transform: `translate3d(0,${Math.round(from)}px,0)` },
        { transform: `translate3d(0,${to}px,0)` },
      ],
      { duration: this.settleMs(), easing: 'ease-out' },
    );

    this.settleAnim.onfinish = () => {
      this.settleAnim = null;
      done();
    };
  }

  private cancelRaf(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.pendingY = null;
  }
}
